import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Loader2, MapPin, Clock3 } from "lucide-react";
import api from "@/lib/api";
import useGeoCurrency, { priceLabel } from "@/lib/useGeoCurrency";
import { optimizeImageUrl } from "@/lib/imageUtils";
import PageBanner from "@/components/PageBanner";

export default function Visas() {
  const [visas, setVisas] = useState([]);
  const [loading, setLoading] = useState(true);
  const currency = useGeoCurrency();
  const [pageEnabled, setPageEnabled] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/visas").then((res) => setVisas(res.data)).catch(() => {}),
      api.get("/page-visibility").then((res) => setPageEnabled(res.data.visas)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  if (!loading && !pageEnabled) {
    return (
      <div data-testid="visas-page-disabled" className="min-h-[60vh] flex items-center justify-center text-[var(--brand-secondary)]">
        This page is currently unavailable.
      </div>
    );
  }

  return (
    <div data-testid="visas-list-page" className="min-h-[70vh] bg-[#FDFCFB]">
      <PageBanner pageKey="visas" eyebrow="Umrah & Visit Visas" title="Visa Services" />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 sm:pb-24 pt-10 sm:pt-0">
        <p data-testid="visas-seo-intro" className="text-center text-sm text-[var(--brand-secondary)] max-w-3xl mx-auto mb-8 leading-relaxed">
          Apply online for Umrah visas, Gulf visit visas and tourist visas with Gulfudaan. We handle document verification, application submission and follow-up with the authorities, keeping you updated at every step. Each visa service below lists processing time, requirements and transparent pricing with no hidden charges.
        </p>
        {loading ? (
          <div className="flex justify-center"><Loader2 className="animate-spin text-[var(--brand-primary)]" size={28} /></div>
        ) : visas.length === 0 ? (
          <p data-testid="visas-empty-state" className="text-center text-[var(--brand-secondary)] py-10">No visas available right now. Please check back soon.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {visas.map((v) => (
              <Link key={v.slug} to={`/visas/${v.slug}`} data-testid={`visa-card-${v.slug}`}
                className="rounded-lg border border-[#E5E1DA] bg-white overflow-hidden hover:-translate-y-1 hover:shadow-lg transition-all duration-300">
                {v.banner_image && (
                  <div className="relative">
                    <img src={optimizeImageUrl(v.banner_image, 500)} alt={v.name} loading="lazy" className="w-full h-44 object-cover" />
                    <span className="absolute top-3 left-3 text-[10px] uppercase tracking-wide font-bold bg-[var(--brand-primary)] text-white px-2.5 py-1 rounded-full flex items-center gap-1"><MapPin size={10} /> {v.country}</span>
                  </div>
                )}
                <div className="p-6">
                  <h3 className="font-heading text-xl text-[#1A1A1A] mb-2 line-clamp-2">{v.name}</h3>
                  <p className="text-xs text-[var(--brand-secondary)] mb-2">{v.visa_type}{v.duration ? ` · ${v.duration}` : ""}</p>
                  <div className="space-y-1.5 text-sm text-[var(--brand-secondary)]">
                    {v.processing_time && <p className="flex items-center gap-1.5"><Clock3 size={13} className="text-[var(--brand-primary)]" /> Processing: {v.processing_time}</p>}
                    {v.price_inr > 0 && <p data-testid={`visa-price-${v.slug}`} className="font-price text-[#1A1A1A]">{priceLabel(v.price_inr, v.price_usd, currency)}{v.requires_payment ? "" : " (pay later)"}</p>}
                  </div>
                  <span className="text-[var(--brand-primary)] text-sm font-medium flex items-center gap-1 mt-4">View & Apply <ArrowRight size={14} /></span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
